import {
  Card,
  CardHeader,
  FormField,
  Button,
  InlineAlert,
  TimePicker,
} from "@constructorfleet/ui/design-system";
import { useEffect, useState } from "react";
import type { BhgCallDashboardProvider } from "../types";
import {
  DEFAULT_BHG_NOTIFICATION_TIME,
  DEFAULT_BHG_NOTIFICATION_TIMEZONE,
  getBhgNotificationTimeError,
  isValidBhgNotificationTime,
  isValidBhgNotificationTimezone,
  BHG_US_TIMEZONE_OPTIONS,
} from "@constructorfleet/bhg-product-core";

export type BhgNotificationScheduleSettingsMessages = {
  notificationTime?: string;
  notificationTimezone?: string;
  provider?: string;
};

export interface BhgNotificationScheduleSettingsProps {
  provider?: Pick<BhgCallDashboardProvider, "fetchSubscription" | "updateNotificationSchedule">;
  disableInitialSync?: boolean;
  dosingHoursJson?: string | null;
  onNotificationScheduleUpdated?: (settings: {
    notificationTime: string;
    notificationTimezone: string;
  }) => void;
  currentTime: string;
  setCurrentTime: (time: string) => void;
  currentTimezone: string;
  setCurrentTimezone: (timezone: string) => void;
  selectedTime: string;
  setSelectedTime: (time: string) => void;
  selectedTimezone: string;
  setSelectedTimezone: (timezone: string) => void;
  saving: boolean;
  setSaving: (saving: boolean) => void;
  message: BhgNotificationScheduleSettingsMessages;
  setMessage: React.Dispatch<React.SetStateAction<BhgNotificationScheduleSettingsMessages>>;
}

export function BhgNotificationScheduleSettings(props: BhgNotificationScheduleSettingsProps) {
  const {
    onNotificationScheduleUpdated,
    provider,
    disableInitialSync,
    dosingHoursJson = null,
    currentTime,
    setCurrentTime,
    currentTimezone,
    setCurrentTimezone,
    selectedTime,
    setSelectedTime,
    selectedTimezone,
    setSelectedTimezone,
    saving,
    setSaving,
    message,
    setMessage,
  } = props;
  const [subscriptionDosingHoursJson, setSubscriptionDosingHoursJson] = useState<string | null>(
    null
  );
  const effectiveDosingHoursJson = dosingHoursJson ?? subscriptionDosingHoursJson;

  useEffect(() => {
    if (disableInitialSync) {
      return;
    }
    if (!provider?.fetchSubscription) {
      return;
    }

    let mounted = true;
    void (async () => {
      const subscription = await provider.fetchSubscription();
      if (!mounted) {
        return;
      }

      const enrollmentTime =
        subscription?.bhgEnrollment?.notificationTime ?? DEFAULT_BHG_NOTIFICATION_TIME;
      const enrollmentTimezone =
        subscription?.bhgEnrollment?.notificationTimezone ?? DEFAULT_BHG_NOTIFICATION_TIMEZONE;
      const safeTime = isValidBhgNotificationTime(enrollmentTime)
        ? enrollmentTime
        : DEFAULT_BHG_NOTIFICATION_TIME;
      const safeTimezone = isValidBhgNotificationTimezone(enrollmentTimezone)
        ? enrollmentTimezone
        : DEFAULT_BHG_NOTIFICATION_TIMEZONE;
      const enrollmentDosingHoursJson = subscription?.bhgEnrollment?.clinicDosingHours ?? null;
      setSubscriptionDosingHoursJson(enrollmentDosingHoursJson);

      setCurrentTime(safeTime);
      setSelectedTime(safeTime);
      setCurrentTimezone(safeTimezone);
      setSelectedTimezone(safeTimezone);
      setMessage((prev) => ({
        ...prev,
        notificationTime: getBhgNotificationTimeError(
          safeTime,
          dosingHoursJson ?? enrollmentDosingHoursJson
        ),
        notificationTimezone: isValidBhgNotificationTimezone(safeTimezone)
          ? undefined
          : "Please select one of the supported US timezones.",
      }));
    })();
    return () => {
      mounted = false;
    };
  }, [
    disableInitialSync,
    dosingHoursJson,
    provider,
    setCurrentTime,
    setCurrentTimezone,
    setMessage,
    setSelectedTime,
    setSelectedTimezone,
  ]);

  useEffect(() => {
    setMessage((prev) => ({
      ...prev,
      notificationTime: getBhgNotificationTimeError(selectedTime, effectiveDosingHoursJson),
    }));
  }, [effectiveDosingHoursJson, selectedTime, setMessage]);

  const isValid = Object.values(message).every((m) => m === undefined);
  const hasChanged = selectedTime !== currentTime || selectedTimezone !== currentTimezone;

  const handleSave = async () => {
    if (!isValid) {
      return;
    }

    setSaving(true);
    setMessage((prev) => ({ ...prev, provider: undefined }));
    const ok = await provider?.updateNotificationSchedule({
      notificationTime: selectedTime,
      notificationTimezone: selectedTimezone,
    });
    setSaving(false);
    if (ok) {
      setCurrentTime(selectedTime);
      setCurrentTimezone(selectedTimezone);
      onNotificationScheduleUpdated?.({
        notificationTime: selectedTime,
        notificationTimezone: selectedTimezone,
      });
      setMessage((prev) => ({ ...prev, provider: "Notification schedule updated successfully." }));
    } else {
      setMessage((prev) => ({
        ...prev,
        provider: "Failed to update notification schedule. Please try again.",
      }));
    }
  };

  return (
    <Card>
      <CardHeader
        title="Notification Schedule"
        meta="Set when you want to receive your BHG call notification."
      />
      <div className="card-body ds-stack">
        <p style={{ fontSize: "0.85rem", color: "var(--cf-muted-text)" }}>
          We will do our best to deliver your BHG call notification at your selected time. Please
          choose as early a time as possible to account for service disruptions and to allow
          adjusting your day's activities for schedules that arise.
        </p>
        <FormField
          label="Preferred notification time"
          htmlFor="dashboard-notification-time"
          errorText={message.notificationTime}
        >
          <TimePicker
            id="dashboard-notification-time"
            value={selectedTime}
            stepMinutes={5}
            onChange={(event) => {
              const next = event.target.value;
              setSelectedTime(next);
              setMessage((prev) => ({
                ...prev,
                notificationTime: getBhgNotificationTimeError(next, effectiveDosingHoursJson),
              }));
            }}
            required
          />
        </FormField>
        <FormField
          label="Timezone"
          htmlFor="dashboard-notification-timezone"
          errorText={message.notificationTimezone}
        >
          <select
            id="dashboard-notification-timezone"
            className="ds-input"
            value={selectedTimezone}
            onChange={(event) => {
              const next = event.target.value;
              setSelectedTimezone(next);
              setMessage((prev) => ({
                ...prev,
                notificationTimezone: isValidBhgNotificationTimezone(next)
                  ? undefined
                  : "Please select one of the supported US timezones.",
              }));
            }}
            required
          >
            {BHG_US_TIMEZONE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </FormField>
        {message.provider ? (
          <InlineAlert tone={message.provider.includes("successfully") ? "success" : "danger"}>
            {message.provider}
          </InlineAlert>
        ) : null}
        {onNotificationScheduleUpdated ? (
          <div className="ds-row">
            <Button
              type="button"
              disabled={!hasChanged || !isValid || saving}
              onClick={() => void handleSave()}
            >
              {saving ? "Saving..." : "Update schedule"}
            </Button>
          </div>
        ) : null}
      </div>
    </Card>
  );
}
